import React, { Component } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Grid, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Api from '../../services/api';
import SecurityAlertService from '../../services/security-alert.service';
import { t } from '../usage/usage-i18n';

// ---------------------------------------------------------------------------
// Item 2 — bloco "Alertas de segurança" da tela de configuração (aba geral). Só Global.
// O que o gestor decide aqui: ligar/desligar por contratante, país base, quem recebe o e-mail (globais + fixos + extras),
// e por regra: ligada?, severidade e (falhas de login) os limiares por usuário e por IP na janela de 1 hora.
// Salva por conta própria (PUT api/security/alerts/settings) no JSONB da tb_config; o serviço de fundo relê a cada rodada (5 min),
// sem deploy nem reinício. O interruptor global (PUT global-switch) só aparece para o id 1 e vale para TODOS os contratantes.
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEVERITY_COLORS = { critical: '#c62828', warning: '#ef6c00', info: '#607D8B' };
// Quais limiares cada regra mostra (as demais só têm ligada/severidade). 2b: recusas (usuário + IP), exportações (usuário), volume (multiplicador).
const RULE_FIELDS = { login_failures: ['perUser', 'perIp'], denial_burst: ['perUser', 'perIp'], export_burst: ['perUser'], volume_anomaly: ['multiplier'] };

export default class SecurityAlertSettingsBlock extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, saving: false, switching: false, error: null, settings: null, emailInput: '' };
    this.save = this.save.bind(this);
    this.toggleGlobal = this.toggleGlobal.bind(this);
    this.addEmail = this.addEmail.bind(this);
  }

  componentDidMount() { this.load(); }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  notify(v, m) { if (this.props.onNotify) this.props.onNotify(v, m); }

  load() {
    SecurityAlertService.settings()
      .then((s) => this.setState({ loading: false, settings: s }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false, error: err.message }); });
  }

  patch(p) { this.setState((st) => ({ settings: { ...st.settings, ...p }, error: null })); }
  patchRule(key, p) { this.setState((st) => ({ settings: { ...st.settings, rules: { ...st.settings.rules, [key]: { ...st.settings.rules[key], ...p } } }, error: null })); }

  addEmail() {
    const { settings, emailInput } = this.state;
    const v = (emailInput || '').trim().toLowerCase();
    if (!v) return;
    if (!EMAIL_RE.test(v)) { this.setState({ error: this.tr('security.alerts.err.email', 'E-mail inválido.') }); return; }
    if (settings.emails.includes(v) || settings.fixedEmails.includes(v)) { this.setState({ emailInput: '' }); return; }
    if (settings.emails.length >= settings.limits.maxEmails) { this.setState({ error: this.tr('security.alerts.err.emailMax', 'Limite de e-mails adicionais atingido.') }); return; }
    this.setState({ settings: { ...settings, emails: [...settings.emails, v] }, emailInput: '', error: null });
  }

  removeEmail(e) { this.patch({ emails: this.state.settings.emails.filter((x) => x !== e) }); }

  // Mesmas cercas da API, antecipadas na tela (a API valida de novo).
  validate() {
    const { settings } = this.state; const lim = settings.limits;
    if (!/^[A-Za-z]{2}$/.test(settings.homeCountry || '')) return this.tr('security.alerts.err.country', 'País base: use o código de 2 letras (ex.: BR).');
    const inRange = (v, min, max) => Number.isFinite(Number(v)) && Number(v) >= min && Number(v) <= max;
    const keys = Object.keys(settings.rules);
    for (let i = 0; i < keys.length; i += 1) {
      const k = keys[i]; const r = settings.rules[k]; const fields = RULE_FIELDS[k] || [];
      if (fields.includes('perUser') && !inRange(r.perUserPerHour, lim.perHourMin, lim.perHourMax)) return `${this.ruleLabel(k)}: ${this.tr('security.alerts.err.threshold', 'limiares entre {min} e {max} por hora.').replace('{min}', lim.perHourMin).replace('{max}', lim.perHourMax)}`;
      if (fields.includes('perIp') && !inRange(r.perIpPerHour, lim.perHourMin, lim.perHourMax)) return `${this.ruleLabel(k)}: ${this.tr('security.alerts.err.threshold', 'limiares entre {min} e {max} por hora.').replace('{min}', lim.perHourMin).replace('{max}', lim.perHourMax)}`;
      if (fields.includes('multiplier') && !inRange(r.volumeMultiplier, lim.multiplierMin, lim.multiplierMax)) return `${this.ruleLabel(k)}: ${this.tr('security.alerts.err.multiplier', 'multiplicador entre {min} e {max}.').replace('{min}', lim.multiplierMin).replace('{max}', lim.multiplierMax)}`;
    }
    return null;
  }

  save() {
    const error = this.validate();
    if (error) { this.setState({ error }); return; }
    this.setState({ saving: true, error: null });
    SecurityAlertService.saveSettings(this.state.settings)
      .then((s) => { this.setState({ saving: false, settings: s }); this.notify('success', this.tr('security.alerts.saved', 'Alertas de segurança salvos. O serviço de fundo aplica na próxima rodada (até 5 minutos).')); })
      .catch((err) => { Api.kickoff(err); this.setState({ saving: false, error: err.message }); this.notify('error', err.message); });
  }

  toggleGlobal(e) {
    const enabled = e.target.checked;
    this.setState({ switching: true });
    SecurityAlertService.setGlobalSwitch(enabled)
      .then((globalEnabled) => { this.patch({ globalEnabled }); this.setState({ switching: false }); this.notify('success', globalEnabled ? this.tr('security.alerts.global.on', 'Alertas ligados para todos os contratantes.') : this.tr('security.alerts.global.off', 'Alertas DESLIGADOS para todos os contratantes (vale por 30 dias ou até religar).')); })
      .catch((err) => { this.setState({ switching: false }); this.notify('error', err.message); });
  }

  help(text) {
    return {
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title={text} placement="top" arrow componentsProps={{ tooltip: { sx: { maxWidth: 320, fontSize: 12, lineHeight: 1.45, whiteSpace: 'normal', padding: '8px 12px' } } }}>
            <HelpOutlineIcon fontSize="small" style={{ color: '#888', cursor: 'help' }} />
          </Tooltip>
        </InputAdornment>
      ),
    };
  }

  ruleLabel(k) { return this.tr(`usage.alerts.rule.${k}`, k); }
  ruleHelp(k) { return this.tr(`security.alerts.rule.${k}.help`, ''); }

  renderRule(key) {
    const { settings } = this.state;
    const r = settings.rules[key];
    if (!r) return null;
    const lim = settings.limits;
    const fields = RULE_FIELDS[key] || [];
    const severityCols = fields.length === 0 ? 7 : fields.length === 1 ? 5 : 3;
    return (
      <Grid item xs={12} key={key}>
        <Box style={{ border: '1px solid #eee', borderRadius: 8, padding: '8px 12px', borderLeft: `4px solid ${r.enabled ? SEVERITY_COLORS[r.severity] || '#999' : '#ccc'}` }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControlLabel control={<Switch checked={!!r.enabled} onChange={(e) => this.patchRule(key, { enabled: e.target.checked })} color="primary" />}
                label={<span><b>{this.ruleLabel(key)}</b><Typography variant="caption" style={{ display: 'block', color: '#666' }}>{this.ruleHelp(key)}</Typography></span>} />
            </Grid>
            <Grid item xs={12} md={severityCols}>
              <TextField select fullWidth size="small" variant="outlined" label={this.tr('usage.alerts.col.severity', 'Severidade')} value={r.severity} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                onChange={(e) => this.patchRule(key, { severity: e.target.value })} disabled={!r.enabled}>
                {settings.severities.map((s) => <option key={s} value={s}>{this.tr(`usage.alerts.severity.${s}`, s)}</option>)}
              </TextField>
            </Grid>
            {fields.includes('perUser') && (
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="number" variant="outlined" label={this.tr('security.alerts.perUser', 'Por usuário / hora')} value={r.perUserPerHour} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: lim.perHourMin, max: lim.perHourMax }} onChange={(e) => this.patchRule(key, { perUserPerHour: e.target.value })} disabled={!r.enabled} />
              </Grid>
            )}
            {fields.includes('perIp') && (
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="number" variant="outlined" label={this.tr('security.alerts.perIp', 'Por IP / hora')} value={r.perIpPerHour} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: lim.perHourMin, max: lim.perHourMax }} onChange={(e) => this.patchRule(key, { perIpPerHour: e.target.value })} disabled={!r.enabled} />
              </Grid>
            )}
            {fields.includes('multiplier') && (
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="number" variant="outlined" label={this.tr('security.alerts.multiplier', 'Vezes acima do normal')} value={r.volumeMultiplier} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: lim.multiplierMin, max: lim.multiplierMax }} onChange={(e) => this.patchRule(key, { volumeMultiplier: e.target.value })} disabled={!r.enabled}
                  InputProps={this.help(this.tr('security.alerts.multiplier.help', 'Alerta quando as linhas devolvidas no dia passam do "Alerta de volume" (acima, em Prazos de sessão) E de tantas vezes a mediana diária do próprio usuário nos últimos 30 dias. Sem 5 dias de histórico, vale o limiar absoluto vezes este número.'))} />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  render() {
    const { loading, saving, switching, error, settings, emailInput } = this.state;
    return (
      <Box style={{ border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginTop: 8 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{this.tr('security.alerts.title', 'Alertas de segurança')}</Typography>
        <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>
          {this.tr('security.alerts.intro', 'O serviço de fundo avalia a cada 5 minutos o que ficou fora do normal (login de país novo, IP de datacenter ou Tor, rajada de falhas de login) e envia um resumo por e-mail aos administradores globais e aos endereços abaixo. Nada aqui bloqueia acesso: é aviso. Mudanças valem na próxima rodada, sem reinício.')}
        </Typography>
        {loading && <CircularProgress size={20} color="secondary" />}
        {!loading && settings && (
          <Grid container spacing={2}>
            {settings.canGlobalSwitch && (
              <Grid item xs={12}>
                <Alert severity={settings.globalEnabled ? 'info' : 'warning'} action={switching ? <CircularProgress size={18} /> : <Switch checked={!!settings.globalEnabled} onChange={this.toggleGlobal} color="primary" />}>
                  <b>{this.tr('security.alerts.global.title', 'Interruptor geral (todos os contratantes)')}</b>{' — '}
                  {settings.globalEnabled ? this.tr('security.alerts.global.onHint', 'ligado. Desligar aqui para todos, sem deploy, se a feature precisar ser pausada.') : this.tr('security.alerts.global.offHint', 'DESLIGADO para todos os contratantes: nenhum alerta é avaliado nem enviado.')}
                </Alert>
              </Grid>
            )}
            {!settings.globalEnabled && !settings.canGlobalSwitch && <Grid item xs={12}><Alert severity="warning">{this.tr('security.alerts.global.offHint', 'DESLIGADO para todos os contratantes: nenhum alerta é avaliado nem enviado.')}</Alert></Grid>}
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={!!settings.enabled} onChange={(e) => this.patch({ enabled: e.target.checked })} color="primary" />} label={this.tr('security.alerts.enabled', 'Alertas ligados neste ambiente')} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={!!settings.notifyGlobalAdmins} onChange={(e) => this.patch({ notifyGlobalAdmins: e.target.checked })} color="primary" />}
                label={<span>{this.tr('security.alerts.notifyGlobals', 'Avisar administradores globais')}<Typography variant="caption" style={{ display: 'block', color: settings.notifyGlobalAdmins ? '#c62828' : '#666' }}>{this.tr('security.alerts.notifyGlobals.help', 'Desligado: só os endereços fixos e adicionais abaixo. Ligado: soma TODO usuário ativo com papel de administrador global deste ambiente.')}</Typography></span>} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" variant="outlined" label={this.tr('security.alerts.homeCountry', 'País base (2 letras)')} value={settings.homeCountry} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                onChange={(e) => this.patch({ homeCountry: (e.target.value || '').toUpperCase() })}
                InputProps={this.help(this.tr('security.alerts.homeCountry.help', 'Login vindo de país diferente deste (e diferente dos países já vistos para a pessoa) gera o alerta "login de país novo".'))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>{this.tr('security.alerts.emails', 'Quem recebe o e-mail')}</Typography>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {settings.fixedEmails.map((e) => <Tooltip key={e} title={this.tr('security.alerts.emails.fixed', 'Fixo (definido pela Mexx; não pode ser removido)')}><Chip size="small" label={e} style={{ backgroundColor: '#eceff1' }} /></Tooltip>)}
                {settings.emails.map((e) => <Chip key={e} size="small" label={e} color="primary" variant="outlined" onDelete={() => this.removeEmail(e)} />)}
              </Box>
              <TextField size="small" variant="outlined" label={this.tr('security.alerts.emails.add', 'Adicionar e-mail')} value={emailInput} InputLabelProps={{ shrink: true }} style={{ minWidth: 320 }}
                onChange={(e) => this.setState({ emailInput: e.target.value, error: null })} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.addEmail(); } }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Button size="small" onClick={this.addEmail}>{this.tr('security.alerts.emails.addBtn', 'Adicionar')}</Button></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2">{this.tr('security.alerts.rules', 'Regras')}</Typography></Grid>
            {settings.ruleKeys.map((k) => this.renderRule(k))}
            {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" onClick={this.save} disabled={saving} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>{this.tr('security.alerts.save', 'Salvar alertas de segurança')}</Button>
            </Grid>
          </Grid>
        )}
        {!loading && !settings && error && <Alert severity="warning">{error}</Alert>}
      </Box>
    );
  }
}
